import React, { Component } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import AlertModal from "./AlertModal";
import PromptModal from "./PromptModal";
import "./Modal.css";
import "./WalkAI.css";
import MultiSelectModal from "./MultiSelectModal";
import Place from "../../Models/Place";
import Common, { API_BASE_URL } from "../../Common/Common";
import Appointment from "../../Models/Event";
import { DateTime } from "luxon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { countries } from "../../Data/Countries";
import { citiesByCountry } from "../../Data/Cities";
import GooglePlaces from "../../Models/GooglePlaces";
import GoogleSearch from "../../Models/GoogleSearch";
import { geocode, setKey, RequestType } from "react-geocode";
import pathImage from "../../assets/images/path.png";
import { uniqueGroupName } from "../../Models/OverpassTrails";
import store from "../../store/store";
import { placeRowFor } from "../../Common/placeRow";
import { applyAIBestTime } from "../../Common/aiDaySchedule";

// Walk entry shape:
// { id, city, country, distanceMin, distanceMax, distanceUnit, circular, dateStart (Luxon), dateEnd (Luxon) }

class WalkAI extends Component {
  constructor(props) {
    super(props);
    setKey(process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY);
    let city = "";
    let country = "Italy";
    const attrs = window.guide_obj.attributes;
    if (attrs.cities && attrs.cities.length > 0) city = attrs.cities[0];
    if (attrs.countries && attrs.countries.length > 0) country = attrs.countries[0];

    const now = DateTime.now().toUTC().plus({ days: 1 }).set({ hour: 9, minute: 0, second: 0 });
    const end = now.plus({ hours: 2 });

    this.state = {
      wizardStep: 1,
      walks: [{ id: Date.now(), city, country, activityType: "walk", distanceMin: "2", distanceMax: "5", distanceUnit: "km", circular: true, startingPlaceId: "", dateStart: now, dateEnd: end, excludedPlaces: [] }],
      editingWalkIdx: 0,

      activityIndicator: false,
      statusText: "",
      generatedWalks: [],   // [{ walkEntry, options: [walkOption] }]
      selectedWalks: {},    // { `${gi}-${oi}`: true/false }

      AlertModalShow: false,
      AlertModalTitle: "",
      AlertModalMessage: "",
      AlertModalButtons: [],

      MultiselectItems: [],
      MultiselectSelectedItems: [],
      MultiselectMode: "uni",
      MultiselectCallback: null,
      MultiselectTitle: "Select",
      MultiselectSearchText: "",
      ShowMultiselect: false,

      cityPromptShow: false,
      cityPromptIdx: 0,
    };
  }

  // ── Alert / multiselect helpers ───────────────────────────────────────────

  MyAlert = (title, message = "") => {
    this.setState({
      AlertModalTitle: title,
      AlertModalMessage: message,
      AlertModalButtons: [{ Title: "OK", Method: null }],
      AlertModalShow: true,
    });
  };

  MultiSelect = (Items, SelectedItems, Mode, Callback, Title) => {
    this.setState({
      MultiselectItems: Items,
      MultiselectSelectedItems: SelectedItems,
      MultiselectMode: Mode,
      MultiselectCallback: Callback,
      MultiselectTitle: Title,
      MultiselectSearchText: "",
      ShowMultiselect: true,
    });
  };

  // ── Walk entry helpers ────────────────────────────────────────────────────

  addWalk = () => {
    const ws = this.state.walks;
    const last = ws[ws.length - 1];
    const newStart = last.dateEnd.plus({ days: 1 }).set({ hour: 9, minute: 0 });
    const newEnd = newStart.plus({ hours: 2 });
    this.setState({
      walks: [...ws, { id: Date.now(), city: last.city, country: last.country, activityType: last.activityType, distanceMin: last.distanceMin, distanceMax: last.distanceMax, distanceUnit: last.distanceUnit, circular: last.circular, startingPlaceId: "", dateStart: newStart, dateEnd: newEnd, excludedPlaces: [] }],
    });
  };

  removeWalk = (idx) => {
    this.setState({ walks: this.state.walks.filter((_, i) => i !== idx) });
  };

  updateWalk = (idx, patch) => {
    this.setState({ walks: this.state.walks.map((w, i) => (i === idx ? { ...w, ...patch } : w)) });
  };

  selectCountry = (idx) => {
    this.setState({ editingWalkIdx: idx }, () => {
      const items = countries.sort().map((c, i) => ({ label: c, name: c, value: c, id: i.toString() }));
      this.MultiSelect(items, [], "uni", this.countrySelected, "Select Country");
    });
  };

  countrySelected = (selected) => {
    if (selected.length < 1) return;
    const country = selected[0].value;
    const idx = this.state.editingWalkIdx;
    const prevCountry = this.state.walks[idx].country;
    // Blanking the city on an actual country change (not a no-op reselect)
    // avoids leaving a stale city from the old country — city choices are
    // scoped to a country. validate() already requires a city before
    // generation can run, so this can't slip through unnoticed.
    const patch = { country };
    if (country !== prevCountry) patch.city = "";
    this.updateWalk(idx, patch);
  };

  WALK_CITY_OTHER = "__other__";

  // Country-first: if the country isn't picked yet, redirect there instead
  // of showing an empty/meaningless city list. Separate from — and doesn't
  // touch — selectPlaceFromGuide, which stays its own affordance for
  // picking an existing guide place instead of a curated/typed city.
  selectWalkCity = (idx) => {
    const w = this.state.walks[idx];
    if (!w.country) {
      this.selectCountry(idx);
      return;
    }
    const cityList = (citiesByCountry[w.country] || []).slice().sort((a, b) => b.popularity - a.popularity);
    if (cityList.length === 0) {
      this._promptWalkCityText(idx);
      return;
    }
    this.setState({ editingWalkIdx: idx }, () => {
      const items = cityList.map((c, i) => ({ label: c.city, name: c.city, value: c.city, id: (i + 1).toString() }));
      items.push({ label: "Type a different city…", name: "other", value: this.WALK_CITY_OTHER, id: (items.length + 1).toString() });
      this.MultiSelect(items, [], "uni", this.walkCitySelected, `Select a city in ${w.country}`);
    });
  };

  walkCitySelected = (selected) => {
    if (!selected || selected.length === 0) return;
    const idx = this.state.editingWalkIdx;
    if (selected[0].value === this.WALK_CITY_OTHER) {
      this._promptWalkCityText(idx);
      return;
    }
    this.updateWalk(idx, { city: selected[0].value });
  };

  // Free-text fallback — for a country with no curated cities, or the
  // curated list's own "type a different city" escape hatch. No geocode
  // validation: this field is labeled "City / Place" and is meant to also
  // accept non-geocodable entries like a region or trailhead area.
  _promptWalkCityText = (idx) => {
    this.setState({ cityPromptShow: true, cityPromptIdx: idx });
  };

  _walkCityTyped = (inputs) => {
    const city = (inputs?.[0]?.value || "").trim();
    if (city === "") return;
    this.updateWalk(this.state.cityPromptIdx, { city });
  };

  selectPlaceFromGuide = (idx) => {
    this.setState({ editingWalkIdx: idx }, () => {
      const items = window.guide_obj.places.placesList
        .filter((p) => p.PType === "Icon" && p.Name)
        .map((p, i) => ({
          label: p.City ? `${p.Name} (${p.City})` : p.Name,
          name: p.Name,
          value: JSON.stringify({ city: p.City || p.Name, country: p.Country || "" }),
          id: i.toString(),
          // Photo, category · visit length, a line of notes (2026-10-17).
          placeRow: placeRowFor(p, { categories: window.guide_obj.categories, user_info: store.getState().user_info }),
        }));
      if (items.length === 0) {
        this.MyAlert("No Places", "No places found in the current guide.");
        return;
      }
      this.MultiSelect(items, [], "uni", this.placeFromGuideSelected, "Select Place from Guide");
    });
  };

  placeFromGuideSelected = (selected) => {
    if (selected.length < 1) return;
    try {
      const { city, country } = JSON.parse(selected[0].value);
      const patch = { city };
      if (country) patch.country = country;
      this.updateWalk(this.state.editingWalkIdx, patch);
    } catch (_) {}
  };

  selectExcludedPlaces = (idx) => {
    this.setState({ editingWalkIdx: idx }, () => {
      const items = window.guide_obj.places.placesList
        .filter((p) => p.PType === "Icon" && p.Name)
        .map((p, i) => ({
          label: p.City ? `${p.Name} (${p.City})` : p.Name,
          name: p.Name,
          value: p.Name,
          id: i.toString(),
          placeRow: placeRowFor(p, { categories: window.guide_obj.categories, user_info: store.getState().user_info }),
        }));
      if (items.length === 0) {
        this.MyAlert("No Places", "No places found in the current guide.");
        return;
      }
      const current = this.state.walks[idx].excludedPlaces.map((name, i) => ({ label: name, name, value: name, id: i.toString() }));
      this.MultiSelect(items, current, "multi", this.excludedPlacesSelected, "Exclude Places");
    });
  };

  excludedPlacesSelected = (selected) => {
    this.updateWalk(this.state.editingWalkIdx, { excludedPlaces: selected.map((s) => s.value) });
  };

  _guideIconPlaces = () =>
    window.guide_obj.places.placesList.filter(
      (p) => p.PType === "Icon" && p.Name && p.Lat && p.Longi
    );

  selectStartingPlace = (idx) => {
    this.setState({ editingWalkIdx: idx }, () => {
      const places = this._guideIconPlaces();
      if (places.length === 0) {
        this.MyAlert("No Places", "No places with coordinates found in the guide.");
        return;
      }
      const items = places.map((p, i) => ({
        label: p.City ? `${p.Name} (${p.City})` : p.Name,
        name: p.Name,
        value: p.ID,
        id: i.toString(),
      }));
      const w = this.state.walks[idx];
      const current = w.startingPlaceId
        ? items.filter((it) => it.value === w.startingPlaceId)
        : [];
      this.MultiSelect(items, current, "uni", this.startingPlaceSelected, "Select Starting Place");
    });
  };

  startingPlaceSelected = (selected) => {
    this.updateWalk(this.state.editingWalkIdx, {
      startingPlaceId: selected.length > 0 ? selected[0].value : "",
    });
  };

  // ── Validation ────────────────────────────────────────────────────────────

  validate = () => {
    for (let i = 0; i < this.state.walks.length; i++) {
      const w = this.state.walks[i];
      if (!w.city.trim()) {
        this.MyAlert("Missing Location", `Walk ${i + 1}: enter a city or place name.`);
        return false;
      }
      if (!w.country.trim()) {
        this.MyAlert("Missing Country", `Walk ${i + 1}: select a country.`);
        return false;
      }
      const dMin = parseFloat(w.distanceMin);
      const dMax = parseFloat(w.distanceMax);
      if (isNaN(dMin) || dMin <= 0) {
        this.MyAlert("Invalid Distance", `Walk ${i + 1}: enter a valid minimum distance.`);
        return false;
      }
      if (isNaN(dMax) || dMax <= dMin) {
        this.MyAlert("Invalid Distance", `Walk ${i + 1}: maximum distance must be greater than minimum.`);
        return false;
      }
      if (w.dateEnd <= w.dateStart) {
        this.MyAlert("Invalid Time", `Walk ${i + 1}: end time must be after start time.`);
        return false;
      }
    }
    return true;
  };

  // ── AI call ───────────────────────────────────────────────────────────────

  buildPrompt = (w) => {
    const dist = `${w.distanceMin}–${w.distanceMax} ${w.distanceUnit}`;
    const activityType = w.activityType || "walk";

    let startingPlaceName = "";
    if (w.startingPlaceId) {
      const sp = this._guideIconPlaces().find((p) => p.ID === w.startingPlaceId);
      if (sp) startingPlaceName = sp.Name;
    }

    let intro, poiContext, unitNoun;
    if (activityType === "bike") {
      const shape = w.circular ? "circular (loop, returning to the start point)" : "one-way (point to point)";
      intro = `Generate 3 different cycling route options in or near ${w.city}, ${w.country}. Each route should be approximately ${dist}, ${shape}. Favor dedicated bike paths, cycling lanes, low-traffic roads, and scenic cycling infrastructure.`;
      poiContext = `Each route must pass through interesting, photogenic, or culturally significant places suitable for cyclists. Include 4-6 points of interest per route, listed in the order they would be visited.`;
      unitNoun = "route";
    } else if (activityType === "hike") {
      const shape = w.circular ? "a loop trail (returning to the trailhead)" : "an out-and-back or point-to-point trail";
      intro = `Generate 3 different hiking trail options in or near ${w.city}, ${w.country}. Each trail should be approximately ${dist}, ${shape}. Prioritize established hiking trails, nature paths, and scenic routes with natural landmarks, viewpoints, and elevation interest.`;
      poiContext = `Each trail must pass through scenic viewpoints, natural landmarks, or historically significant sites along the way. Include 4-6 points of interest per trail, listed in the order they would be encountered.`;
      unitNoun = "trail";
    } else {
      const shape = w.circular ? "circular (returning to the start point)" : "one-way (point to point)";
      intro = `Generate 3 different walking tour options in or near ${w.city}, ${w.country}. Each walk should be approximately ${dist}, ${shape}.`;
      poiContext = `Each walk must pass through interesting, must-see, historically significant, or culturally important places. Include 4-6 points of interest per walk, listed in the order they would be visited.`;
      unitNoun = "walk";
    }

    return (
      `${intro} ` +
      (startingPlaceName ? `Each ${unitNoun} must start at "${startingPlaceName}". ` : "") +
      `${poiContext} ` +
      `For each POI provide its exact, searchable name (as it appears on Google Maps) and a short description of why it is notable. ` +
      (w.excludedPlaces && w.excludedPlaces.length > 0
        ? `Do NOT include any of these places: ${w.excludedPlaces.join(", ")}. `
        : "") +
      `Respond ONLY with RFC8259 JSON, no commentary, in this exact format: ` +
      `[{"WalkName":"name","Description":"overall ${unitNoun} description","EstimatedDurationMinutes":90,` +
      `"POIs":[{"Name":"exact Google Maps name","Description":"why it is notable","BestTime":"morning, afternoon, sunset or evening — the best time of day to visit, \"\" if it doesn't matter"}],` +
      `"Directions":["Step 1: start at ...","Step 2: ..."]}]`
    );
  };

  createWalks = () => {
    if (!this.validate()) return;
    this.setState({ activityIndicator: true, statusText: "Asking AI…", wizardStep: 2, generatedWalks: [], selectedWalks: {} });
    this._generateNext(0, []);
  };

  _generateNext = (idx, accumulated) => {
    if (idx >= this.state.walks.length) {
      const initialSelected = {};
      accumulated.forEach((g, gi) =>
        g.options.forEach((_, oi) => { initialSelected[`${gi}-${oi}`] = true; })
      );
      this.setState({ generatedWalks: accumulated, selectedWalks: initialSelected, activityIndicator: false, statusText: "" });
      return;
    }
    const w = this.state.walks[idx];
    const actLabel = w.activityType === "bike" ? "routes" : w.activityType === "hike" ? "trails" : "walks";
    this.setState({ statusText: `Generating ${actLabel} for ${w.city}… (${idx + 1}/${this.state.walks.length})` });

    const url = `${API_BASE_URL}/travel_questionsGPT5.6-luna.php?q=${encodeURIComponent(this.buildPrompt(w))}`;
    fetch(url)
      .then((r) => r.json())
      .then((response) => {
        let answer = "";
        try { answer = response.choices[0].message.content || ""; } catch (_) {}

        if (!answer.trim()) {
          this.MyAlert("Empty Response", `Walk ${idx + 1}: the AI returned a blank response. Try again.`);
          this.setState({ activityIndicator: false, statusText: "", wizardStep: 1 });
          return;
        }
        let options = [];
        try {
          options = this._parseWalkJSON(answer);
        } catch (e) {
          console.log("WalkAI parse error:", e.message, "\nRaw:", answer);
          this.MyAlert("Parse Error", `Walk ${idx + 1}: could not parse the AI response. Try again.`);
          this.setState({ activityIndicator: false, statusText: "", wizardStep: 1 });
          return;
        }
        this._generateNext(idx + 1, [...accumulated, { walkEntry: w, options }]);
      })
      .catch((err) => {
        this.MyAlert("Network Error", err.message);
        this.setState({ activityIndicator: false, statusText: "", wizardStep: 1 });
      });
  };

  _parseWalkJSON = (raw) => {
    let s = raw.replace(/```json\s*/gi, "").replace(/```\s*/g, "");
    const arrIdx = s.indexOf("[");
    const objIdx = s.indexOf("{");
    let start = -1;
    if (arrIdx !== -1 && (objIdx === -1 || arrIdx < objIdx)) start = arrIdx;
    else if (objIdx !== -1) start = objIdx;
    if (start !== -1) s = s.substring(start);
    try {
      const p = JSON.parse(s);
      return Array.isArray(p) ? p : [p];
    } catch (firstErr) {
      const last = s.lastIndexOf("}");
      if (last !== -1) {
        let repaired = s.substring(0, last + 1);
        if (s.trimStart().startsWith("[")) repaired += "]";
        try {
          const p = JSON.parse(repaired);
          return Array.isArray(p) ? p : [p];
        } catch (_) {}
      }
      throw firstErr;
    }
  };

  // ── Google Places geocoding ───────────────────────────────────────────────

  // Geocodes the walk city to {lat, lng} using react-geocode (same key as
  // TripAI). Returns numbers, not a "lat,lng" string, since
  // searchPlacesNewAPI (below) takes lat/lng separately.
  _geocodeCity = (walkEntry) => {
    return new Promise((resolve) => {
      geocode(RequestType.ADDRESS, `${walkEntry.city}, ${walkEntry.country}`)
        .then(({ results }) => {
          if (!results || results.length === 0) { resolve(null); return; }
          const { lat, lng } = results[0].geometry.location;
          resolve({ lat, lng });
        })
        .catch(() => resolve(null));
    });
  };

  // Finds each POI via GooglePlaces text search biased to the city lat/lng.
  // Returns an array of { Name, Description, Lat, Lng }. Uses
  // searchPlacesNewAPI (provider-switchable — google/hasdata/openwebninja,
  // see PLACES_PROVIDER in Common.js) instead of the old
  // searchPlaces+getQuickDetails legacy pair, which was hardcoded to
  // Google's legacy Places API regardless of that setting. This call site
  // doesn't consume Category or thread a label through, so it was a safe,
  // low-risk first candidate for the swap — found while scoping
  // PLACES_PROVIDER coverage gaps, 2026-09-24.
  _geocodePOIs = (pois, walkEntry, cityCoords) => {
    return new Promise((resolve) => {
      const results = [];
      const next = (idx) => {
        if (idx >= pois.length) { resolve(results); return; }
        const poi = pois[idx];
        const gp = new GooglePlaces();
        gp.searchPlacesNewAPI(
          `${poi.Name} near ${walkEntry.city}, ${walkEntry.country}`,
          null, cityCoords.lat, cityCoords.lng, 10000,
          (detailed) => {
            if (detailed && detailed.length > 0 && detailed[0].Lat && detailed[0].Longi) {
              results.push({ Name: poi.Name, Description: poi.Description || "", BestTime: poi.BestTime || "", Lat: detailed[0].Lat, Lng: detailed[0].Longi });
            }
            setTimeout(() => next(idx + 1), 300);
          }
        );
      };
      next(0);
    });
  };

  // Fire-and-forget photo fetch (mirrors TripAI's searchGoogleImages): tries
  // the paid PLACES_PROVIDER source first (real business-listing photo, now
  // that OpenWeb Ninja/HasData is a paid subscription), falling back to the
  // pre-existing free Custom Search on a miss. Added 2026-09-24.
  _fetchPhoto = (searchText, placeId, lat, lng) => {
    const finish = (photo, url_m) => {
      const place = window.guide_obj.places.getPlace(placeId);
      if (!place) return;
      place.Photo = photo;
      place.Photo_m = url_m ?? photo;
      try { place.Info = Place.createImageHTML(Place.proxyImageUrl(place.Photo_m), null, "medium", null, place.Photo) + (place.Info || ""); } catch (e) {}
    };
    const runFallback = () => {
      GoogleSearch.searchImages(searchText, (imageList, id) => {
        if (!imageList || imageList.length === 0) return;
        finish(imageList[0].url, imageList[0].url_m);
      }, placeId);
    };
    const paidSource = GooglePlaces.getPhotoProviderChain();
    paidSource.searchPlacePhoto(searchText, (photo) => {
      if (photo) { finish(photo); return; }
      runFallback();
    }, lat, lng);
  };

  // Builds a PolyLine Points string by connecting the geocoded POIs in order.
  // Returns "lng,lat:lng,lat:..." — circular walks close back to the first POI.
  _getWalkingRoute = (geocodedPOIs, circular) => {
    if (geocodedPOIs.length === 0) return Promise.resolve("");
    const pts = geocodedPOIs.map((p) => `${parseFloat(p.Lng).toFixed(6)},${parseFloat(p.Lat).toFixed(6)}`);
    if (circular && geocodedPOIs.length > 1) pts.push(pts[0]);
    return Promise.resolve(pts.join(":"));
  };

  // ── Adding walks to guide ─────────────────────────────────────────────────

  _deleteEventsInRange = (dateStart, dateEnd) => {
    const startMs = dateStart.toMillis();
    const endMs = dateEnd.toMillis();
    const toDelete = window.guide_obj.schedule.events.filter((ev) => {
      const itin = typeof ev.Itinerary === "undefined" ? "Main" : ev.Itinerary;
      if (itin !== "Main") return false;
      const evStart = DateTime.fromISO(ev.Start).toMillis();
      const evEnd = DateTime.fromISO(ev.End).toMillis();
      return evStart < endMs && evEnd > startMs;
    });
    toDelete.forEach((ev) => window.guide_obj.schedule.deleteEvent(ev));
  };

  addSelected = async () => {
    const { generatedWalks, selectedWalks } = this.state;
    const anySelected = Object.values(selectedWalks).some(Boolean);
    if (!anySelected) {
      this.MyAlert("Nothing Selected", "Select at least one walk to add.");
      return;
    }

    this.setState({ activityIndicator: true, statusText: "Locating city…" });

    for (let gi = 0; gi < generatedWalks.length; gi++) {
      const { walkEntry, options } = generatedWalks[gi];

      // 1. Geocode the city once per walk entry (mirrors TripAI's proceedNext1)
      const cityLatLng = await this._geocodeCity(walkEntry);
      if (!cityLatLng) {
        this.MyAlert("Location Error", `Could not locate ${walkEntry.city}, ${walkEntry.country}. Check spelling and try again.`);
        this.setState({ activityIndicator: false, statusText: "", wizardStep: 1 });
        return;
      }

      let deletedRange = false;

      for (let oi = 0; oi < options.length; oi++) {
        if (!selectedWalks[`${gi}-${oi}`]) continue;
        const walk = options[oi];

        // 2. Find each POI via GooglePlaces biased to city lat/lng (mirrors TripAI's googleTextSearch)
        this.setState({ statusText: `Finding places for "${walk.WalkName}"…` });
        const geocodedPOIs = await this._geocodePOIs(walk.POIs || [], walkEntry, cityLatLng);

        // 3. Resolve optional starting place from guide
        let startingPoint = null;
        if (walkEntry.startingPlaceId) {
          const sp = this._guideIconPlaces().find((p) => p.ID === walkEntry.startingPlaceId);
          if (sp) startingPoint = { Lat: sp.Lat, Lng: sp.Longi, Name: sp.Name };
        }

        // 4. Build PolyLine points — prepend starting place if provided
        const routePOIs = startingPoint ? [startingPoint, ...geocodedPOIs] : geocodedPOIs;
        this.setState({ statusText: `Routing "${walk.WalkName}"…` });
        const points = await this._getWalkingRoute(routePOIs, walkEntry.circular);

        // 5. Delete conflicting events once per walk entry time range
        if (!deletedRange) {
          this._deleteEventsInRange(walkEntry.dateStart, walkEntry.dateEnd);
          deletedRange = true;
        }

        // 6. Add path, POI places and event — photos fetched async afterward (mirrors TripAI's Finish)
        this._commitWalkToGuide(walk, walkEntry, geocodedPOIs, points, startingPoint);
      }
    }

    // Mirror TripAI: kick off guide update immediately, then UI refresh after photo fetches settle
    if (this.props.update_guide) this.props.update_guide();
    setTimeout(() => {
      if (this.props.update_ui) this.props.update_ui("Main");
      this.setState({ activityIndicator: false, statusText: "" });
      this.props.onHide();
    }, 3000);
  };

  _commitWalkToGuide = (walk, walkEntry, geocodedPOIs, points, startingPoint = null) => {
    const activityType = walkEntry.activityType || "walk";
    const travelMode = activityType === "bike" ? "bicycling" : "walking";
    const defaultName = activityType === "bike" ? "Bike Route" : activityType === "hike" ? "Hike" : "Walk";
    const existingGroupNames = window.guide_obj.groups.map((g) => g.name);
    const groupName = uniqueGroupName(walk.WalkName || defaultName, existingGroupNames);
    const groupId = window.guide_obj.addGroup(groupName);

    // Convert distance range to both units
    const dMin = parseFloat(walkEntry.distanceMin);
    const dMax = parseFloat(walkEntry.distanceMax);
    let distStr = "";
    if (!isNaN(dMin) && !isNaN(dMax) && dMin > 0 && dMax > 0) {
      if (walkEntry.distanceUnit === "km") {
        distStr = `${dMin}–${dMax} km / ${(dMin * 0.621371).toFixed(1)}–${(dMax * 0.621371).toFixed(1)} mi`;
      } else {
        distStr = `${dMin}–${dMax} mi / ${(dMin * 1.60934).toFixed(1)}–${(dMax * 1.60934).toFixed(1)} km`;
      }
    }

    // Build Google Maps directions URL — startingPoint is origin when provided
    let mapsUrl = "";
    const allRoutePoints = startingPoint ? [startingPoint, ...geocodedPOIs] : geocodedPOIs;
    if (allRoutePoints.length >= 2) {
      const originPt = allRoutePoints[0];
      const origin = `${originPt.Lat},${originPt.Lng}`;
      const destPt = walkEntry.circular ? originPt : allRoutePoints[allRoutePoints.length - 1];
      const destination = `${destPt.Lat},${destPt.Lng}`;
      const middle = walkEntry.circular ? allRoutePoints.slice(1) : allRoutePoints.slice(1, -1);
      const waypointsParam = middle.length > 0
        ? `&waypoints=${middle.map((p) => `${p.Lat},${p.Lng}`).join("%7C")}`
        : "";
      mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypointsParam}&travelmode=${travelMode}`;
    }

    // Build info HTML: description + distance + directions
    let infoHtml = walk.Description ? `<p>${walk.Description}</p>` : "";
    if (distStr) infoHtml += `<p><strong>Distance:</strong> ${distStr}</p>`;
    if (walk.Directions && walk.Directions.length > 0)
      infoHtml += `<ol>${walk.Directions.map((d) => `<li>${d}</li>`).join("")}</ol>`;

    // 1. PolyLine path place — only when we have a real multi-point route
    const placeIds = [];
    const validPointCount = points ? points.split(":").filter((s) => s.includes(",")).length : 0;
    if (validPointCount >= 2) {
      const pathPlace = new Place(window.guide_obj.places);
      pathPlace.PType = "PolyLine";
      pathPlace.Name = walk.WalkName || defaultName;
      pathPlace.Group = [groupId];
      pathPlace.City = walkEntry.city;
      pathPlace.Country = walkEntry.country;
      const pathPhotoUrl = window.location.origin + pathImage;
      pathPlace.Photo = pathPhotoUrl;
      pathPlace.Photo_m = pathPhotoUrl;
      pathPlace.Info = Place.createImageHTML(pathPhotoUrl, null, "medium") + infoHtml;
      if (mapsUrl) {
        pathPlace.Hlink = mapsUrl;
        pathPlace.Link = { type: "url", value: mapsUrl, offset: "0" };
      }
      pathPlace.Points = points;
      const pathId = window.guide_obj.places.addPlace(pathPlace);
      if (pathId != null) placeIds.push(pathId);
    }

    // 2. Icon places for each geocoded POI
    geocodedPOIs.forEach((poi) => {
      if (!poi.Lat || !poi.Lng) return;
      const existing = window.guide_obj.places.getPlacesFromName(poi.Name);
      if (existing) {
        if (!existing.Group.includes(groupId)) existing.Group = [...existing.Group, groupId];
        if (!placeIds.includes(existing.ID)) placeIds.push(existing.ID);
        applyAIBestTime(existing, poi.BestTime); // only if the place has none
        return;
      }
      const p = new Place(window.guide_obj.places);
      p.PType = "Icon";
      p.Name = poi.Name;
      p.Lat = poi.Lat;
      p.Longi = poi.Lng;
      p.City = walkEntry.city;
      p.Country = walkEntry.country;
      p.MustSee = true;
      p.Info = poi.Description ? `<p>${poi.Description}</p>` : "";
      applyAIBestTime(p, poi.BestTime);
      p.Group = [groupId];
      const poiId = window.guide_obj.places.addPlace(p);
      placeIds.push(poiId);
      // Fetch photo async after place is added (mirrors TripAI's searchGoogleImages)
      this._fetchPhoto(`${poi.Name} near ${walkEntry.city}, ${walkEntry.country}`, poiId, poi.Lat, poi.Lng);
    });

    // 3. Schedule event
    const event = new Appointment(window.guide_obj.schedule);
    event.Subject = walk.WalkName || defaultName;
    event.Body = "";
    event.Itinerary = "Main";
    event.Start = walkEntry.dateStart;
    event.End = walkEntry.dateEnd;
    event.Places = placeIds.join(",");
    window.guide_obj.schedule.addEvent(event);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  render() {
    const { wizardStep, walks, activityIndicator, statusText, generatedWalks, selectedWalks } = this.state;
    const totalSelected = Object.values(selectedWalks).filter(Boolean).length;

    if (this.state.AlertModalShow)
      return (
        <AlertModal
          show={this.state.AlertModalShow}
          onHide={() => this.setState({ AlertModalShow: false })}
          AlertModalTitle={this.state.AlertModalTitle}
          AlertModalMessage={this.state.AlertModalMessage}
          AlertModalButtons={this.state.AlertModalButtons}
        />
      );

    if (this.state.ShowMultiselect)
      return (
        <MultiSelectModal
          show={this.state.ShowMultiselect}
          onHide={() => this.setState({ ShowMultiselect: false })}
          MultiselectItems={this.state.MultiselectItems}
          MultiselectSelectedItems={this.state.MultiselectSelectedItems}
          MultiselectMode={this.state.MultiselectMode}
          MultiselectCallback={this.state.MultiselectCallback}
          MultiselectTitle={this.state.MultiselectTitle}
          MultiselectSearchText={this.state.MultiselectSearchText}
        />
      );

    if (this.state.cityPromptShow)
      return (
        <PromptModal
          show={true}
          onHide={() => this.setState({ cityPromptShow: false })}
          PromptModalTitle="City or Place"
          PromptModalMessage={`What city or place in ${this.state.walks[this.state.cityPromptIdx]?.country || "this country"}?`}
          PromptModalInputs={[{ name: "City", value: this.state.walks[this.state.cityPromptIdx]?.city || "" }]}
          PromptModalButtons={[
            { Title: "Save", Method: (inputs) => this._walkCityTyped(inputs) },
            { Title: "Cancel", Method: null },
          ]}
        />
      );

    return (
      <div className="modal">
        <Modal
          {...this.props}
          dialogClassName="wai-dialog"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          scrollable={false}
        >
          <Modal.Body style={{ padding: 0 }}>
            <div className="wai-shell">

              {/* ── Header ── */}
              <div className="wai-header">
                <div className="wai-header-icon">
                  <FontAwesomeIcon icon={solid("person-walking")} />
                </div>
                <h2 className="wai-title">Path Creator</h2>
                {wizardStep === 2 && !activityIndicator && (
                  <span className="wai-step-badge">Review Walks</span>
                )}
                {activityIndicator && (
                  <div className="wai-spinner">
                    <Spinner animation="border" size="sm" variant="secondary" />
                  </div>
                )}
                <button className="wai-close" onClick={() => this.props.onHide()}>
                  <FontAwesomeIcon icon={solid("xmark")} />
                </button>
              </div>

              {/* ── Step 1: Configure ── */}
              {wizardStep === 1 && (
                <>
                  <div className="wai-scroll">
                    <span className="wai-section-label">AI picks the route &amp; POIs · Google Maps routes the path</span>

                    <span className="wai-section-label">Walks</span>
                    {walks.map((w, idx) => (
                      <div key={w.id} className="wai-walk-card">
                        <div className="wai-walk-header">
                          <span className="wai-walk-num">Walk {idx + 1}</span>
                          {walks.length > 1 && (
                            <button className="wai-walk-delete" onClick={() => this.removeWalk(idx)}>
                              <FontAwesomeIcon icon={solid("xmark")} />
                            </button>
                          )}
                        </div>

                        {/* Country */}
                        <div className="wai-row-item" onClick={() => this.selectCountry(idx)}>
                          <span className="wai-row-key">Country</span>
                          {w.country && <span className="wai-row-value">{w.country}</span>}
                          <span className="wai-row-chevron"><FontAwesomeIcon icon={solid("chevron-right")} /></span>
                        </div>
                        <div className="wai-divider" />

                        {/* City / Place — picks from the selected country's curated
                            city list, with a "type a different city" fallback.
                            "Pick from guide" stays a separate, independent button. */}
                        <div className="wai-row-item" onClick={() => this.selectWalkCity(idx)}>
                          <span className="wai-row-key">City</span>
                          {w.city && <span className="wai-row-value">{w.city}</span>}
                          <span className="wai-row-chevron"><FontAwesomeIcon icon={solid("chevron-right")} /></span>
                          <button
                            className="wai-pick-btn"
                            title="Pick from guide"
                            onClick={(e) => { e.stopPropagation(); this.selectPlaceFromGuide(idx); }}
                          >
                            <FontAwesomeIcon icon={solid("location-dot")} />
                          </button>
                        </div>
                        <div className="wai-divider" />

                        {/* Activity Type */}
                        <div className="wai-input-row">
                          <span className="wai-input-label">Activity</span>
                          <div className="wai-activity-toggle">
                            {["walk", "bike", "hike"].map((type) => (
                              <button
                                key={type}
                                className={`wai-activity-btn${w.activityType === type ? " active" : ""}`}
                                onClick={(e) => { e.stopPropagation(); this.updateWalk(idx, { activityType: type }); }}
                              >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="wai-divider" />

                        {/* Distance range */}
                        <div className="wai-input-row">
                          <span className="wai-input-label">Distance</span>
                          <input
                            className="wai-input-field wai-dist-input"
                            type="number"
                            min="0.1"
                            step="0.5"
                            placeholder="min"
                            value={w.distanceMin}
                            onChange={(e) => this.updateWalk(idx, { distanceMin: e.target.value })}
                          />
                          <span className="wai-dist-dash">–</span>
                          <input
                            className="wai-input-field wai-dist-input"
                            type="number"
                            min="0.1"
                            step="0.5"
                            placeholder="max"
                            value={w.distanceMax}
                            onChange={(e) => this.updateWalk(idx, { distanceMax: e.target.value })}
                          />
                          <select
                            className="wai-unit-select"
                            value={w.distanceUnit}
                            onChange={(e) => this.updateWalk(idx, { distanceUnit: e.target.value })}
                          >
                            <option value="km">km</option>
                            <option value="miles">miles</option>
                          </select>
                        </div>
                        {(() => {
                          const mn = parseFloat(w.distanceMin);
                          const mx = parseFloat(w.distanceMax);
                          if (isNaN(mn) || isNaN(mx) || mn <= 0 || mx <= 0) return null;
                          if (w.distanceUnit === "km") {
                            return <span className="wai-dist-hint">{(mn * 0.621371).toFixed(1)}–{(mx * 0.621371).toFixed(1)} mi</span>;
                          }
                          return <span className="wai-dist-hint">{(mn * 1.60934).toFixed(1)}–{(mx * 1.60934).toFixed(1)} km</span>;
                        })()}
                        <div className="wai-divider" />

                        {/* Circular */}
                        <div className="wai-check-row" onClick={() => this.updateWalk(idx, { circular: !w.circular })}>
                          <div className={`wai-check-box${w.circular ? " checked" : ""}`}>
                            {w.circular && <FontAwesomeIcon icon={solid("check")} />}
                          </div>
                          <span className="wai-check-label">
                            {w.activityType === "bike" ? "Loop route (return to start)" : w.activityType === "hike" ? "Loop trail (return to start)" : "Circular (return to start)"}
                          </span>
                        </div>
                        <div className="wai-divider" />

                        {/* From */}
                        <div className="wai-date-row">
                          <span className="wai-row-key">From</span>
                          <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                            selected={Common.JSDateFromLuxonDate(w.dateStart)}
                            onChange={(date) => this.updateWalk(idx, { dateStart: Common.LuxonDateFromJSDate(date) })}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            timeCaption="time"
                            dateFormat="MMM d, yyyy HH:mm"
                          />
                        </div>
                        <div className="wai-divider" />

                        {/* To */}
                        <div className="wai-date-row">
                          <span className="wai-row-key">To</span>
                          <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                            selected={Common.JSDateFromLuxonDate(w.dateEnd)}
                            onChange={(date) => this.updateWalk(idx, { dateEnd: Common.LuxonDateFromJSDate(date) })}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            timeCaption="time"
                            dateFormat="MMM d, yyyy HH:mm"
                          />
                        </div>
                        <div className="wai-divider" />

                        {/* Starting place */}
                        {(() => {
                          const sp = w.startingPlaceId
                            ? this._guideIconPlaces().find((p) => p.ID === w.startingPlaceId)
                            : null;
                          return (
                            <div className="wai-row-item" onClick={() => this.selectStartingPlace(idx)}>
                              <span className="wai-row-key">Starting place</span>
                              {sp
                                ? <span className="wai-row-value">{sp.Name}</span>
                                : <span className="wai-row-value wai-row-placeholder">None</span>}
                              <span className="wai-row-chevron"><FontAwesomeIcon icon={solid("chevron-right")} /></span>
                            </div>
                          );
                        })()}
                        <div className="wai-divider" />

                        {/* Exclude places */}
                        <div className="wai-row-item" onClick={() => this.selectExcludedPlaces(idx)}>
                          <span className="wai-row-key">Exclude these places</span>
                          {w.excludedPlaces.length > 0
                            ? <span className="wai-row-value">{w.excludedPlaces.length} selected</span>
                            : <span className="wai-row-value wai-row-placeholder">None</span>}
                          <span className="wai-row-chevron"><FontAwesomeIcon icon={solid("chevron-right")} /></span>
                        </div>
                      </div>
                    ))}

                    <div className="wai-add-btn-row">
                      <button className="wai-add-walk-btn" onClick={() => this.addWalk()}>
                        <FontAwesomeIcon icon={solid("plus")} /> Add Activity
                      </button>
                    </div>

                    <p className="wai-hint">
                      AI will suggest 3 walk options per entry, each with curated must-see stops.
                      Google Maps will then route the precise walking path between them.
                      Existing schedule events in the same time range will be replaced.
                    </p>
                  </div>

                  <div className="wai-footer">
                    <div className="wai-footer-spacer" />
                    <button className="wai-cancel-btn" onClick={() => this.props.onHide()}>Cancel</button>
                    <button className="wai-primary-btn" onClick={() => this.createWalks()}>
                      <FontAwesomeIcon icon={solid("wand-magic-sparkles")} /> Create Paths
                    </button>
                  </div>
                </>
              )}

              {/* ── Step 2: Loading ── */}
              {wizardStep === 2 && activityIndicator && (
                <div className="wai-loading">
                  <Spinner animation="border" variant="secondary" />
                  <p>{statusText || "Working…"}</p>
                </div>
              )}

              {/* ── Step 2: Review ── */}
              {wizardStep === 2 && !activityIndicator && generatedWalks.length > 0 && (
                <>
                  <div className="wai-scroll">
                    <span className="wai-section-label">Select walks to add — Google Maps will route the paths when you tap Add</span>

                    {generatedWalks.map((group, gi) => (
                      <div key={gi}>
                        {generatedWalks.length > 1 && (
                          <span className="wai-group-label">
                            {group.walkEntry.city}, {group.walkEntry.country} — {group.walkEntry.activityType === "bike" ? "Bike" : group.walkEntry.activityType === "hike" ? "Hike" : "Walk"} {group.walkEntry.distanceMin}–{group.walkEntry.distanceMax} {group.walkEntry.distanceUnit}
                          </span>
                        )}
                        {group.options.map((option, oi) => {
                          const key = `${gi}-${oi}`;
                          const checked = !!selectedWalks[key];
                          return (
                            <div
                              key={oi}
                              className={`wai-option-card${checked ? " selected" : ""}`}
                              onClick={() => this.setState({ selectedWalks: { ...selectedWalks, [key]: !checked } })}
                            >
                              <div className="wai-option-header">
                                <div className={`wai-check-box${checked ? " checked" : ""}`}>
                                  {checked && <FontAwesomeIcon icon={solid("check")} />}
                                </div>
                                <span className="wai-option-name">{option.WalkName}</span>
                              </div>
                              <div className="wai-option-meta">
                                {option.EstimatedDurationMinutes && (
                                  <span><FontAwesomeIcon icon={solid("clock")} /> {option.EstimatedDurationMinutes} min</span>
                                )}
                                {(() => {
                                  const mn = parseFloat(group.walkEntry.distanceMin);
                                  const mx = parseFloat(group.walkEntry.distanceMax);
                                  if (isNaN(mn) || mn <= 0) return null;
                                  const unit = group.walkEntry.distanceUnit;
                                  const other = unit === "km"
                                    ? `${(mn * 0.621371).toFixed(1)}–${(mx * 0.621371).toFixed(1)} mi`
                                    : `${(mn * 1.60934).toFixed(1)}–${(mx * 1.60934).toFixed(1)} km`;
                                  return (
                                    <span>
                                      <FontAwesomeIcon icon={solid("person-walking")} />
                                      {" "}{mn}–{mx} {unit} / {other}
                                    </span>
                                  );
                                })()}
                                {(option.POIs || []).length > 0 && (
                                  <span><FontAwesomeIcon icon={solid("star")} /> {option.POIs.length} stops</span>
                                )}
                              </div>
                              {option.Description && (
                                <p className="wai-option-desc">{option.Description}</p>
                              )}
                              {(option.POIs || []).length > 0 && (
                                <div className="wai-pois">
                                  {option.POIs.map((poi, pi) => (
                                    <span key={pi} className="wai-poi-chip">
                                      <FontAwesomeIcon icon={solid("location-dot")} /> {poi.Name}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {(option.Directions || []).length > 0 && (
                                <details className="wai-directions" onClick={(e) => e.stopPropagation()}>
                                  <summary>Directions ({option.Directions.length} steps)</summary>
                                  <ol className="wai-directions-list">
                                    {option.Directions.map((d, di) => <li key={di}>{d}</li>)}
                                  </ol>
                                </details>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  <div className="wai-footer">
                    <button className="wai-cancel-btn" onClick={() => this.setState({ wizardStep: 1 })}>
                      <FontAwesomeIcon icon={solid("chevron-left")} /> Back
                    </button>
                    <div className="wai-footer-spacer" />
                    <button className="wai-cancel-btn" onClick={() => this.props.onHide()}>Cancel</button>
                    <button
                      className="wai-primary-btn"
                      onClick={() => this.addSelected()}
                      disabled={totalSelected === 0}
                    >
                      <FontAwesomeIcon icon={solid("map-location-dot")} />
                      {" "}Add {totalSelected > 0 ? `${totalSelected} ` : ""}Path{totalSelected !== 1 ? "s" : ""}
                    </button>
                  </div>
                </>
              )}

            </div>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

export default WalkAI;
